import { fetchQuote } from "../constants/api";
import { getRouteDetails } from "../helpers/getRouteDetails";
import { ethers } from "ethers-v6";
import { getConfig } from "../constants/config";
import tokenService from "../../common/tokenService";

export const getRoute = async (
  tokenIn,
  tokenOut,
  amountIn,
  options = { isRaw: false }
) => {
  try {
    // Validate inputs
    if (!tokenIn || !tokenOut || !amountIn) {
      throw new Error("Error getting route: Arguments missing!");
    }

    // Normalize addresses to lowercase for consistent lookups
    tokenIn = tokenIn.toLowerCase();
    tokenOut = tokenOut.toLowerCase();

    // Check if raw amount

    if (typeof amountIn === "bigint" && !options.isRaw) {
      console.warn(
        `Route has parameters 'isRaw:${options.isRaw}' but amountIn is BigInt. Type BigInt can only represent raw amounts. Option 'isRaw' set to 'true'`
      );
      options.isRaw = true;
    }

    // Get config for non-token data
    const config = getConfig();
    const {
      nativeAddress,
      wrappedNativeAddress,
      chainId,
      additionalTokens,
      overrideDefaultTokens,
    } = config;

    // Get updated token list with user's configuration applied
    const tokens = await tokenService.getAsyncTokenList(
      chainId,
      additionalTokens,
      overrideDefaultTokens,
      nativeAddress
    );

    const includesNative =
      tokenIn === nativeAddress || tokenOut === nativeAddress;

    if (
      tokenIn === tokenOut ||
      (tokenIn === wrappedNativeAddress && tokenOut === nativeAddress) ||
      (tokenIn === nativeAddress && tokenOut === wrappedNativeAddress)
    ) {
      console.error(
        "Error getting route: Cannot swap between same tokens or do wrap/unwrap!"
      );
      return {
        route: undefined,
        rebate: undefined,
        amountInFormatted: undefined,
        amountIn: amountIn,
        amountOut: undefined,
        amountOutFormatted: undefined,
        pathPercentages: undefined,
        pathCount: undefined,
        includesNative: undefined,
        tokenIn,
        tokenOut,
        tokenInUsd: undefined,
        tokenOutUsd: undefined,
        priceImpactPercentage: undefined,
        swapFee: undefined,
      };
    }
    if (options.isRaw)
      amountIn = ethers.formatUnits(
        amountIn,
        tokens[tokenIn].attributes.decimals
      );

    // Prepare API request parameters
    const routeUrl = "/route";
    const params = {
      tokenIn:
        tokenIn.toLowerCase() === nativeAddress ? "0x0" : tokenIn.toLowerCase(),
      tokenOut:
        tokenOut.toLowerCase() === nativeAddress
          ? "0x0"
          : tokenOut.toLowerCase(),
      amountIn: amountIn.toString(),
    };

    // Fetch route from the API
    const routeResult = await fetchQuote(routeUrl, params);

    if (!routeResult?.data) {
      throw new Error("Error getting route: No data received from API.");
    }

    const route = routeResult.data;
    const rebate = routeResult.rebate;
    const tokenInUsd = routeResult.tokenInUSD;
    const tokenOutUsd = routeResult.tokenOutUSD;
    const swapFee = routeResult.swapFee;
    const priceImpactPercentage = routeResult.priceImpactPercentage;

    // Calculate route details
    const routeDetails = await getRouteDetails(route);

    return {
      route,
      rebate,
      amountInFormatted: amountIn,
      amountIn: ethers.parseUnits(
        amountIn,
        tokens[tokenIn].attributes.decimals
      ),
      amountOut: routeDetails.totalOut,
      amountOutFormatted: routeDetails.totalOutFormatted,
      pathPercentages: routeDetails.pathPercentages,
      pathCount: routeDetails.pathCount,
      includesNative: includesNative,
      tokenIn,
      tokenOut,
      tokenInUsd,
      tokenOutUsd,
      priceImpactPercentage,
      swapFee,
    };
  } catch (error) {
    // Log custom error message and return undefined
    console.error(
      "Cannot get quote:",
      error.message,
      "This might mean token addresses are wrong or token does not have an active pool or have not been indexed by Symphony yet."
    );
    return {
      route: undefined,
      rebate: undefined,
      amountInFormatted: undefined,
      amountIn: undefined,
      amountOut: undefined,
      amountOutFormatted: undefined,
      pathPercentages: undefined,
      pathCount: undefined,
      includesNative: undefined,
      tokenIn,
      tokenOut,
      tokenInUsd: undefined,
      tokenOutUsd: undefined,
      priceImpactPercentage: undefined,
      swapFee: undefined,
    };
  }
};
